import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useBillingsWipeImpact, useWipeBillingsFiscalYear } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import type { BillingsFiscalYear } from '@/types'

/**
 * Remove one fiscal year's uploaded sales data so it can be re-uploaded cleanly.
 *
 * THIS ONE REALLY DELETES. Everywhere else in the CRM a delete is a flag and the record
 * can come back. Here the row genuinely goes, because a retained row keeps its frozen
 * exchange rate: re-uploading a soft-deleted year would restore the same wrong numbers,
 * which is precisely the thing this button exists to fix.
 *
 * So the dialog does three things a normal confirm does not:
 *
 *   it counts first     the impact is fetched before anything is typed, so the figures
 *                       on screen are this year's real ones, not a description
 *   it names survivors  targets, mappings, reps and reattributions are listed with
 *                       counts, so the promise can be checked afterwards rather than
 *                       taken on trust
 *   it asks for the year the confirmation string is "FY2027", not "yes". A reflexive
 *                       click cannot produce it, and it names the specific thing being
 *                       destroyed. The server checks it too.
 *
 * The reattribution point is worth reading twice: overrides key on the content hash
 * rather than a row id, so re-uploading the same file re-applies every one of them
 * automatically. That is what makes the cycle safe, and it is stated on screen rather
 * than left for somebody to hope about.
 */
interface WipeFiscalYearDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  years: BillingsFiscalYear[]
}

export function WipeFiscalYearDialog({ open, onOpenChange, years }: WipeFiscalYearDialogProps) {
  const { toast } = useToast()
  const wipe = useWipeBillingsFiscalYear()
  const [fy, setFy] = useState<number | undefined>(undefined)
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')

  const impact = useBillingsWipeImpact(fy, open)

  useEffect(() => {
    if (open) {
      setFy(years[0]?.fiscal_year)
      setConfirm('')
      setError('')
    }
  }, [open, years])

  const expected = fy ? `FY${fy}` : ''
  const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })

  const handleWipe = async () => {
    if (!fy) return
    setError('')
    try {
      const result = await wipe.mutateAsync({ fy, confirm })
      toast({
        title: `FY${result.fiscal_year} sales data removed`,
        description: `${result.lines_removed.toLocaleString()} lines deleted. Re-upload the source reports for that year when you are ready.`,
      })
      onOpenChange(false)
    } catch (err) {
      setError(explainError(err, 'Could not wipe that fiscal year.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Wipe sales data for a fiscal year</DialogTitle>
          <DialogDescription>
            Removes the uploaded QuickBooks lines for one year so you can upload the source reports
            again. Nothing else is touched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="wipe-fy">Fiscal year</Label>
            <select
              id="wipe-fy"
              className="mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={fy ?? ''}
              onChange={(e) => {
                setFy(Number(e.target.value))
                // A confirmation typed for one year must not carry over to another.
                setConfirm('')
              }}
              disabled={wipe.isPending}
            >
              {years.map((y) => (
                <option key={y.fiscal_year} value={y.fiscal_year}>
                  {y.label} ({y.line_count.toLocaleString()} lines)
                </option>
              ))}
            </select>
          </div>

          {impact.isLoading && (
            <p className="text-sm text-muted-foreground">Working out what that would remove…</p>
          )}

          {impact.data && (
            <>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  This permanently removes {impact.data.line_count.toLocaleString()} lines
                </AlertTitle>
                <AlertDescription>
                  {money(impact.data.amount)} across {impact.data.import_count}{' '}
                  {impact.data.import_count === 1 ? 'upload' : 'uploads'}, dated{' '}
                  {String(impact.data.first_date ?? '').slice(0, 10)} to{' '}
                  {String(impact.data.last_date ?? '').slice(0, 10)}. Unlike everything else in the
                  CRM this cannot be undone, because a recoverable row would keep its original
                  exchange rate and come back with the same figures.
                </AlertDescription>
              </Alert>

              <div className="rounded-md border p-3">
                <p className="text-sm font-medium mb-2">Kept, untouched</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>{impact.data.kept.targets} monthly targets</li>
                  <li>{impact.data.kept.rep_mappings} rep mappings</li>
                  <li>{impact.data.kept.product_mappings} product mappings</li>
                  <li>{impact.data.kept.reps} reps on the roster</li>
                  <li>
                    {impact.data.kept.reattributions.toLocaleString()} line reattributions, which
                    re-apply on their own when you upload the year again
                  </li>
                </ul>
              </div>
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription role="alert">{error}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="wipe-confirm">Type {expected} to confirm</Label>
            <Input
              id="wipe-confirm"
              className="mt-1"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={expected}
              autoComplete="off"
              aria-describedby="wipe-confirm-help"
              disabled={wipe.isPending}
            />
            <p id="wipe-confirm-help" className="text-xs text-muted-foreground mt-1">
              Naming the year is deliberate: a reflexive yes cannot produce it.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={wipe.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleWipe}
            disabled={
              wipe.isPending || !fy || confirm.trim().toUpperCase() !== expected.toUpperCase()
            }
          >
            {wipe.isPending ? 'Removing…' : `Permanently remove ${expected} data`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
